import { useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { getMomentTz } from "@dexteel/mesf-core";
import type { Moment } from "moment";
import { TaskDateSection } from "./TaskDateSection";

const moment = getMomentTz();

interface FormData {
  timestamp: Moment | null;
  operatorNumber: string;
}

interface Props {
  open: boolean;
  /** Name of the equipment to set online/offline */
  equipmentName: string | null;
  /** Type of equipment for display (e.g., "Roof", "Sidewall") */
  equipmentType: string;
  action: "online" | "offline";
  /** PartId for loading task history in the DateTimePicker section */
  partId: number | null;
  /** Name of the currently online equipment (only needed when action is "online" and another equipment is already online) */
  /** PartId of the currently online equipment that will be displaced (swap scenario) */
  occupantPartId?: number | null;
  /** Last task timestamp on the target location (when previous equipment was uninstalled) */
  locationLastTimestamp?: Date | null;
  currentOnlineEquipmentName: string | null;
  isLoading: boolean;
  onClose: () => void;
  onConfirm: (operatorNumber: number, timestamp: Date | null) => void;
  /** Whether to validate against CASTER_EQP_INITIAL_DATETIME setting. Only for Caster mold module. */
  validateAgainstInitialDatetime?: boolean;
}

export const SetEquipmentStatusModal = ({
  open,
  equipmentName,
  equipmentType,
  action,
  partId,
  occupantPartId,
  locationLastTimestamp,
  currentOnlineEquipmentName,
  isLoading,
  onClose,
  onConfirm,
  validateAgainstInitialDatetime = false,
}: Props) => {
  const { control, handleSubmit, reset, trigger } = useForm<FormData>({
    defaultValues: {
      timestamp: null,
      operatorNumber: "",
    },
    mode: "onChange",
  });

  // Reset fields when modal opens
  useEffect(() => {
    if (open) {
      reset({
        timestamp: moment(),
        operatorNumber: "",
      });
    }
  }, [open, reset]);

  const onSubmit = (data: FormData) => {
    onConfirm(
      parseInt(data.operatorNumber, 10),
      data.timestamp?.toDate() ?? null,
    );
  };

  const isOnline = action === "online";
  const title = isOnline
    ? `Set ${equipmentType} Online - Confirmation`
    : `Set ${equipmentType} Offline - Confirmation`;
  const confirmLabel = isOnline ? "Set Online" : "Set Offline";
  const confirmColor = isOnline ? "success" : "warning";

  // Check if there's another equipment that will be set offline
  const willReplaceEquipment =
    isOnline &&
    currentOnlineEquipmentName &&
    currentOnlineEquipmentName !== equipmentName;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {equipmentType}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {equipmentName || "-"}
            </Typography>
          </Box>

          {isOnline ? (
            <Typography variant="body2" color="text.secondary">
              This will set the {equipmentType.toLowerCase()} as the active{" "}
              {equipmentType.toLowerCase()} in the EAF.
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              This will remove the {equipmentType.toLowerCase()} from the EAF
              and mark it as offline.
            </Typography>
          )}

          {willReplaceEquipment ? (
            <Alert severity="warning" sx={{ mt: 1 }}>
              <Typography variant="body2">
                <strong>{currentOnlineEquipmentName}</strong> is currently
                online and will be set <strong>Offline</strong> automatically.
              </Typography>
            </Alert>
          ) : null}

          {/* Date selection with task history */}
          <TaskDateSection
            control={control}
            trigger={trigger}
            partId={partId}
            open={open}
            action={isOnline ? "install" : "uninstall"}
            occupantPartId={occupantPartId}
            locationLastTimestamp={isOnline ? locationLastTimestamp : undefined}
            validateAgainstInitialDatetime={validateAgainstInitialDatetime}
          />

          <Controller
            name="operatorNumber"
            control={control}
            rules={{
              required: "Operator number is required",
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="Operator Number"
                size="small"
                fullWidth
                error={!!error}
                helperText={error?.message || "Enter your Operator Number"}
                inputProps={{
                  inputMode: "numeric",
                }}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  field.onChange(value);
                }}
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          color={confirmColor as "success" | "warning"}
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size="1rem" /> : undefined}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
